/**
 * TextInput — editorial style: hairline bottom border ONLY (no full box),
 * generous padding, serif placeholder italic. Variants: default, error.
 * On focus: bottom border thickens to 2px in accent (oxblood) per UX §8.4.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface TextInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** Optional helper text rendered beneath the input. */
  helperText?: string;
  /** When set, the input is wired to an error caption via aria-describedby. */
  errorText?: string;
}

const BASE = cn(
  'block w-full bg-transparent',
  'border-0 border-b border-ink-300',
  'px-0 py-3',
  'font-sans text-base text-ink-900',
  'placeholder:text-ink-400 placeholder:italic placeholder:font-serif',
  'transition-[border-color,border-width] duration-200 ease-out-slow',
  'focus:border-b-2 focus:border-accent focus:outline-none',
  'disabled:opacity-40 disabled:pointer-events-none',
);

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    { invalid, helperText, errorText, className, id, ...rest },
    ref,
  ) {
    const reactId = React.useId();
    const inputId = id ?? `txt_${reactId}`;
    const helperId = helperText ? `${inputId}-help` : undefined;
    const errorId = errorText ? `${inputId}-err` : undefined;
    const isInvalid = invalid || Boolean(errorText);

    const describedBy =
      [errorId, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <>
        <input
          ref={ref}
          id={inputId}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          className={cn(
            BASE,
            isInvalid &&
              'border-error-muted border-b-2 focus:border-error-muted',
            className,
          )}
          {...rest}
        />
        {errorText ? (
          <p
            id={errorId}
            role="alert"
            className="mt-2 text-xs italic text-error-muted"
          >
            {errorText}
          </p>
        ) : null}
        {helperText && !errorText ? (
          <p id={helperId} className="mt-2 text-xs text-ink-500">
            {helperText}
          </p>
        ) : null}
      </>
    );
  },
);
